// SyncWorklistSection — the RETAINED sync-failure worklist (UI_VISION §8 step 3),
// as its OWN section on the product page.
//
// TITLED "Not synced / TBD" since 2026-10-11 (Alex: untagged parts "need to go
// somewhere, they need an action"). "TBD" says every row here waits on a decision.
// TITLED "Not synced" since 2026-08-20. Alex, on finding the same five rows in the
// sync review above and in this grid: "there is a way that we should call the
// unsync list! or Error sync list." "Sync worklist" named the ACTIVITY and left the
// reader to work out what was in it; "Not synced" names the CONTENT — the parts
// that did not become components, which is the one thing every row here has in
// common. It also stays true for the rows that are refusals rather than errors
// (double-count, would-duplicate-a-manual-row), which "Error" would have overstated.
// The review panel above now shows only the COUNT and points here by this name.
//
// It used to render inside the 3D MODEL block, under the Preview button. It
// belongs beside COMPONENTS (Alex, 2026-07-29): the worklist is the NEGATIVE
// SPACE of the components grid — the parts that did NOT become components. The
// 3D MODEL block is about the model (thumbnail, ref, preview trigger); what the
// last preview refused to write is a list about the COMPONENTS.
//
// Reads `onshape_sync_issues` via GET /api/products/:id/sync-issues. Lifecycle is
// server-side (recordSyncIssues): recurring issues bump last_seen, absent ones
// auto-resolve, returning ones reopen.
//
// REFRESH RE-JUDGES FIRST (Alex, 2026-10-11: "When updating the database, to
// correct sync issues, should it data update when refreshing the panel?"). load()
// posts sync-recheck-cached before it reads: the last read is replanned against
// today's catalog at 0 Onshape calls, so a row fixed in the DB leaves this list
// on Refresh — the same moment the tally line already counted it as fixed.
//
// **AN OPEN ROW IS READ-ONLY, and deliberately so** (Alex, 2026-07-20): "to really
// dismiss something, it will have to be in Onshape as a Non BOM item" — the model
// is the only authority, so the one way to close a live row is to fix it (in
// Onshape or the catalog) and re-sync. No dismiss, no resolve, no status control
// on an open row: a human override would drift out of date against the model that
// produced it.
//
// ONLY OPEN ROWS ARE SHOWN (Alex, 2026-10-11: "I do not believe it is useful to
// keep grey out what has been solved in Not Sync / TBD"). The list is a to-do
// list: a fixed row in it is noise, and the manual clear (2026-08-20) was a chore
// that only existed to remove that noise. The server still keeps resolved rows
// (they reopen if the issue comes back); this view just does not draw them.
//
// Colour-blind safe (RULES 3): `kind` is a WORD (ISSUE_TAG); the problem title
// carries ⚠ and a bold count, not only red.
//
// NO PENDING BOX (2026-10-12). It used to show a "Pending from the last preview"
// box above the grid, with its own OK and Remove buttons: the clean rows a read
// had planned but not yet written. Alex: every part "must go in the component
// grid or the Not sync / TBD grid (or the non applicable)" — a fourth place was
// one too many. The Refresh replan now WRITES its clean half (writeSyncPlan), so
// those rows land in Components and nothing is left waiting here.
//
// Everything here is 0 Onshape calls.

import { useState, useEffect, useCallback, useRef } from 'react';
import DataGrid from './DataGrid.jsx';
import SectionTitle from './SectionTitle.jsx';
import ReasonCell from './ReasonCell.jsx';
import { LINK } from '../icons.js';

// ISSUE_TAG lived in CompsSyncPanel until that panel retired (ON-21 step 4,
// 2026-08-21) — the words moved to their last reader.
const ISSUE_TAG = {
  unregistered: 'unregistered',   // no / malformed db_id
  uncategorized: 'no category',   // carries an id but no $ Pricing category — ON-40
  untagged: 'untagged',           // no id and no category — was skipped in silence until 2026-10-11
  unresolved: 'unresolved',       // family + config doesn't pick a size
  'no-mass': 'no mass',           // Onshape reports no mass (material unassigned, or an assembly BOM)
  'no-density': 'no density',     // area line missing density/thickness
  'double-count': 'double-count', // would duplicate a manual comp
  'no-driver': 'no qty_driver',   // kg/m² line with no driver
  data: 'driver ≠ unit',          // driver and unit contradict
};

// last_seen is an ISO timestamp written by recordSyncIssues. Minutes is the
// finest useful grain — the question a row answers is "was this still true at
// the last preview?", not which second it was written.
function stamp(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

const mono = value => (
  value
    ? <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-mono)' }}>{value}</span>
    : <span style={{ color: 'var(--th-text-disabled)' }}>—</span>
);

// Column order is the reading order: `kind` and `part` are what Alex scans,
// `reason` is what he acts on, so it is the widest and wraps.
const WORKLIST_COLS = [
  {
    key: 'type', label: 'Kind', width: 110,
    render: row => (
      <span style={{
        fontSize: 'var(--fs-micro)', padding: '0 5px', borderRadius: 3,
        border: '1px solid var(--th-status-deprecated)', color: 'var(--th-status-deprecated)',
      }}>{ISSUE_TAG[row.type] || row.type}</span>
    ),
  },
  // The part LINKS to its own Onshape view (config included) when the BOM told
  // us where it lives — Alex, 2026-08-21: "capture the link to the faulty
  // Onshape part instead of chasing it". Part-studio rows have no per-part URL;
  // they fall back to plain text and the product's own Open-in-Onshape.
  {
    key: 'part_name', label: 'Part', width: 190,
    render: row => row.view_href
      ? <a href={row.view_href} target="_blank" rel="noopener noreferrer"
          style={{ color: 'var(--th-navy)', textDecoration: 'underline' }}
          title="Open this part in Onshape (its own document, at this configuration)">
          {row.part_name || '(unnamed)'} {LINK}
        </a>
      : (row.part_name || '(unnamed)'),
  },
  // Short form in the row, the whole sentence on hover — the ReasonCell standard.
  { key: 'reason', label: 'Reason', width: 360, render: row => <ReasonCell text={row.reason} /> },
  // WHAT IT POINTS AT, not the raw string. `fam:491` means nothing to a reader;
  // "Insulation — Foam 1"" does. The raw value is shown ONLY when it failed to
  // resolve, because then the raw value IS the finding (db_id malformed).
  {
    key: 'db_id_raw', label: 'Catalog item', width: 190,
    render: row => (row.ref_label
      ? <span>{row.ref_label}</span>
      : <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 'var(--fs-mono)', color: 'var(--th-status-deprecated)' }}>
          {row.db_id_raw || '—'}
        </span>),
  },
  { key: 'config_id', label: 'config_id', width: 120, render: row => mono(row.config_id) },
  {
    key: 'qty', label: 'Qty · unit', width: 100, align: 'right',
    render: row => (row.qty == null ? '—' : `${row.qty}${row.unit ? ` ${row.unit}` : ''}`),
  },
  { key: 'last_seen', label: 'Last checked', width: 130, render: row => stamp(row.last_seen) },
];

export default function SyncWorklistSection({ productId, onshapeRef, refreshToken, onPromoted, topRule = true }) {
  const [issues, setIssues] = useState(null);
  const [collapsed, setCollapsed] = useState(true);

  // Through a ref: both hosts pass an inline arrow, and a callback in load's
  // deps would re-run the recheck on every parent render.
  const promotedRef = useRef(onPromoted);
  promotedRef.current = onPromoted;

  const load = useCallback(async () => {
    // Re-judge the last read against today's catalog BEFORE reading the list
    // (0 Onshape calls). The server also WRITES the clean half into Components,
    // so when it changed anything the components grid must re-read too. A
    // failure here is not fatal: the stored list is still the last honest
    // answer, just possibly behind a DB fix.
    try {
      const r = await fetch(`/api/products/${productId}/sync-recheck-cached`, { method: 'POST' });
      const b = r.ok ? await r.json().catch(() => null) : null;
      const w = b?.written;
      if (w && (w.added || w.updated || w.removed)) promotedRef.current?.();
    } catch { /* fall through to the stored list */ }
    fetch(`/api/products/${productId}/sync-issues`)
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        const rows = (Array.isArray(list) ? list : []).filter(i => i.status === 'open');
        setIssues(rows);
        // OPEN BY DEFAULT WHEN SOMETHING IS OPEN (Alex, 2026-07-31: "if there is
        // a Sync Issue, the issue window must open by default"). A collapsed
        // worklist is a worklist nobody reads — and the count in the title only
        // says HOW MANY, never WHICH. Still collapses when everything is
        // resolved, so a clean product stays quiet.
        if (rows.length) setCollapsed(false);
      })
      .catch(() => setIssues([]));
  }, [productId]);

  // refreshToken is bumped when a Preview lands — the server has just recorded
  // that run's blocked+flagged rows, so the list must re-read. Losing this makes
  // a stale list look correct, which is the worst failure mode here.
  useEffect(() => { setIssues(null); load(); }, [load, refreshToken]);
  useEffect(() => { setCollapsed(true); }, [productId]);

  // No ref = nothing has ever been previewed and nothing ever will be, so the
  // section would be empty furniture on the other ~189 products. With a ref,
  // `0 open` is meaningful — it says the model and the catalog agree.
  if (!onshapeRef) return null;

  const openCount = (issues || []).length;

  return (
    <section style={topRule ? { marginTop: 14, paddingTop: 8, borderTop: '2px solid var(--th-border)' } : undefined}>
      <SectionTitle
        count={issues == null ? null : `${openCount} open`}
        problem={openCount > 0}
        collapsed={collapsed}
        onToggle={() => setCollapsed(c => !c)}
      >Not synced / TBD</SectionTitle>
      {!collapsed && (
        <>
          {/* The only place the closing rule is stated. */}
          <div style={{ fontSize: 'var(--fs-micro)', color: 'var(--th-text-hint)', marginBottom: 4 }}>
            A row leaves this list when the model stops reporting it — fix it in the catalog
            (then Refresh, no Onshape calls) or in Onshape (then Sync).
          </div>
          <DataGrid
            rows={issues || []}
            columns={WORKLIST_COLS}
            loading={issues == null}
            emptyMessage="Nothing held back — the last preview reported no issues."
            storageKey="product-sync-worklist"
          />
        </>
      )}
    </section>
  );
}
